import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateStatutoryParams,
  DeleteStatutoryParams,
  GetAWEParams,
  GetStatutoryDetailParams,
  GetStatutoryLeaveListParams,
  GetStatutoryLeaveListResponse,
  GetStatutoryPaymentListParams,
  GetStatutoryPaymentListResponse,
  ResumeStatutoryRecordParams,
  StopStatutoryRecordParams,
  UpdateStatutoryRecordParams,
} from '../types';
import { STATUTORY_PAYMENT_TYPE } from '@nxsys/constants/statutory';
import { showToast } from '@nxsys/utils/toast';
import { appIntl } from '@nxsys/helpers/Common';

export const getStatutoryLeaveRecords = createAsyncThunk<
  GetStatutoryLeaveListResponse,
  GetStatutoryLeaveListParams
>(
  'statutory/getStatutoryLeaveRecords',
  async (params: GetStatutoryLeaveListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.statutory.leave, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAWEStatutory = createAsyncThunk<
  any,
  GetAWEParams
>(
  'statutory/getAWEStatutory',
  async (params: GetAWEParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.statutory.awe, {
        params,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const getEndpointByStatutoryType = (type: (typeof STATUTORY_PAYMENT_TYPE)[keyof typeof STATUTORY_PAYMENT_TYPE]) => {
  switch (type) {
    case STATUTORY_PAYMENT_TYPE.SSP:
      return endPoints.statutory.ssp;
    case STATUTORY_PAYMENT_TYPE.SMP:
      return endPoints.statutory.smp;
    case STATUTORY_PAYMENT_TYPE.SAP:
      return endPoints.statutory.sap;
    case STATUTORY_PAYMENT_TYPE.SPP_BIRTH:
      return endPoints.statutory.spp;
    case STATUTORY_PAYMENT_TYPE.SPP_ADOPTION:
      return endPoints.statutory.sppa;
    default:
      return '';
  }
}

export const createStatutory = createAsyncThunk<any, CreateStatutoryParams>(`/createStatutory`, async (params, { rejectWithValue }) => {
  try {
    const { type, subcontractorId } = params;
    const newParams: any = { ...params };
    delete newParams.type;
    delete newParams.subcontractorId;
    const endpoint = getEndpointByStatutoryType(type);
    const response = await jwtAxios.post(`${endpoint}/${subcontractorId}`, newParams);
    return response.data.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const deleteStatutory = createAsyncThunk<
  any,
  DeleteStatutoryParams
>(
  'statutory/deleteStatutory',
  async (params: DeleteStatutoryParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(endPoints.statutory.leave, {
        data: params,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getStatutoryDetail = createAsyncThunk<
  any,
  GetStatutoryDetailParams
>(
  'statutory/getStatutoryDetail',
  async (params: GetStatutoryDetailParams, { rejectWithValue }) => {
    try {
      const { type, id } = params;
      const endpoint = getEndpointByStatutoryType(type);
      const response = await jwtAxios.get(`${endpoint}/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const stopStatutoryRecord = createAsyncThunk<any, StopStatutoryRecordParams>(`/stopStatutoryRecord`, async (params, { rejectWithValue }) => {
  try {
    const { type, id } = params;
    const newParams: any = { ...params };
    delete newParams.type;
    delete newParams.id;
    let endpoint = '';
    switch (type) {
      case STATUTORY_PAYMENT_TYPE.SSP:
        endpoint = endPoints.statutory.stopSsp;
        break;
      case STATUTORY_PAYMENT_TYPE.SMP:
        endpoint = endPoints.statutory.stopSmp;
        break;
      case STATUTORY_PAYMENT_TYPE.SAP:
        endpoint = endPoints.statutory.stopSap;
        break;
      case STATUTORY_PAYMENT_TYPE.SPP_BIRTH:
        endpoint = endPoints.statutory.stopSpp;
        break;
      case STATUTORY_PAYMENT_TYPE.SPP_ADOPTION:
        endpoint = endPoints.statutory.stopSppa;
        break;
      default:
        return rejectWithValue('Invalid statutory type');
    }
    const response = await jwtAxios.post(`${endpoint}/${id}`, newParams);
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const resumeStatutoryRecord = createAsyncThunk<any, ResumeStatutoryRecordParams>(`/resumeStatutoryRecord`, async (params, { rejectWithValue }) => {
  try {
    const { type, id } = params;
    const newParams: any = { ...params };
    delete newParams.type;
    delete newParams.id;
    let endpoint = '';
    switch (type) {
      case STATUTORY_PAYMENT_TYPE.SSP:
        endpoint = endPoints.statutory.resumeSsp;
        break;
      case STATUTORY_PAYMENT_TYPE.SMP:
        endpoint = endPoints.statutory.resumeSmp;
        break;
      case STATUTORY_PAYMENT_TYPE.SAP:
        endpoint = endPoints.statutory.resumeSap;
        break;
      case STATUTORY_PAYMENT_TYPE.SPP_BIRTH:
        endpoint = endPoints.statutory.resumeSpp;
        break;
      case STATUTORY_PAYMENT_TYPE.SPP_ADOPTION:
        endpoint = endPoints.statutory.resumeSppa;
        break;
      default:
        return rejectWithValue('Invalid statutory type');
    }
    const response = await jwtAxios.post(`${endpoint}/${id}`, newParams);
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});


export const createTimeSheetStatutory = createAsyncThunk<any, any>(`/createTimeSheetStatutory`, async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.post(`${endPoints.statutory.timesheets}`, params);
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const updateStatutoryRecord = createAsyncThunk<
  any,
  UpdateStatutoryRecordParams
>(
  'statutory/updateStatutoryRecord',
  async (params: UpdateStatutoryRecordParams, { rejectWithValue }) => {
    try {
      const { type } = params;
      const newParams: any = { ...params };
      delete newParams.type;
      const response = await jwtAxios.put(`${endPoints.statutory.payment}/${type}`, newParams);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getStatutoryPaymentRecords = createAsyncThunk<
  GetStatutoryPaymentListResponse,
  GetStatutoryPaymentListParams
>(
  'statutory/getStatutoryPaymentRecords',
  async (params: GetStatutoryPaymentListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.statutory.records, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateStatutoryRecordById = createAsyncThunk<
  any,
  any
>(
  'statutory/updateStatutoryRecordById',
  async (params: any, { rejectWithValue }) => {
    try {
      const { type, id } = params;
      const newParams: any = { ...params };
      delete newParams.type;
      delete newParams.id;
      const response = await jwtAxios.put(`${endPoints.statutory.leave}/${type}/${id}`, newParams);
      if (response?.data?.error) {
        showToast({
          type: 'error',
          message: response?.data?.error,
        });
      } else {
        showToast({
          type: 'success',
          message: appIntl().formatMessage({ id: 'statutory.record.update.getSuccess' }),
        });
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const statutoryActs = {
  getStatutoryLeaveRecords,
  getAWEStatutory,
  createStatutory,
  deleteStatutory,
  getStatutoryDetail,
  stopStatutoryRecord,
  resumeStatutoryRecord,
  updateStatutoryRecord,
  getStatutoryPaymentRecords,
  createTimeSheetStatutory,
  updateStatutoryRecordById,
};
